import {
  ClipboardList,
  GraduationCap,
  BookMinusIcon,
  RefreshCw,
  Target,
  Lightbulb,
  Brain,
  MessageSquare,
  Award,
} from "lucide-react";
import { tC } from "../../utils/TranslateWithContentLanguage";
import CardImage from "../tool_components/CardImage";
import { Button } from "@core/index";
import FormativeAssessmentCardPDF from "./FormativeAssessmentCardPdf";
import PdfDownloadButton from "../pdf/PdfDownloadButton";
import useImagePdf from "../pdf/useImagePdf";
import { FormativeAssessmentResponseDto } from "@packages/api/madlenAPI.schemas";
import PdfCustomizationModal from "@features/common/components/pdf-customization-modal";
import { useAppSelector } from "@packages/store/hooks";
import { useState, useEffect } from "react";
import EditableMarkdown from "../tool_components/EditableMarkdown";

const FormativeAssessmentCard = ({
  assessment,
  onCreateAgain,
}: {
  assessment: FormativeAssessmentResponseDto;
  onCreateAgain?: () => void;
}) => {
  const finalImageUrl = useImagePdf(assessment.imageUrl);
  const contentLanguage = assessment.contentLanguage || "tr";
  const [showPdfCustomizationModal, setShowPdfCustomizationModal] =
    useState(false);
  const [activeSection, setActiveSection] = useState<string>("pedagogical");
  const customizationSettings = useAppSelector(
    (state) => state.user.teacher?.preferences?.pdfOptions,
  );

  // Assessment sections with color themes
  const assessmentSections = [
    {
      id: "pedagogical",
      title: tC(contentLanguage, "formativeAssessment.pedagogicalApproach"),
      shortTitle: tC(
        contentLanguage,
        "formativeAssessment.pedagogicalApproach",
      ),
      icon: Brain,
      color: "#3B82F6", // Blue
      bgColor: "bg-blue-500",
      gradient: "from-blue-500 to-indigo-600",
      darkGradient: "dark:from-blue-600 dark:to-indigo-700",
      lightBg: "bg-blue-50",
      darkBg: "dark:bg-blue-950/40",
      borderColor: "border-blue-400",
      condition: assessment.instructionalFlow?.pedagogical_approach,
    },
    {
      id: "phases",
      title: tC(contentLanguage, "formativeAssessment.learningPhases"),
      shortTitle: tC(contentLanguage, "formativeAssessment.learningPhases"),
      icon: ClipboardList,
      color: "#10B981", // Emerald
      bgColor: "bg-emerald-500",
      gradient: "from-emerald-500 to-green-600",
      darkGradient: "dark:from-emerald-600 dark:to-green-700",
      lightBg: "bg-emerald-50",
      darkBg: "dark:bg-emerald-950/40",
      borderColor: "border-emerald-400",
      condition:
        assessment.instructionalFlow?.learning_phases &&
        assessment.instructionalFlow.learning_phases.length > 0,
    },
    {
      id: "methodology",
      title: tC(contentLanguage, "formativeAssessment.assessmentMethodology"),
      shortTitle: tC(
        contentLanguage,
        "formativeAssessment.assessmentMethodology",
      ),
      icon: Target,
      color: "#F59E0B", // Amber
      bgColor: "bg-amber-500",
      gradient: "from-amber-500 to-orange-600",
      darkGradient: "dark:from-amber-600 dark:to-orange-700",
      lightBg: "bg-amber-50",
      darkBg: "dark:bg-amber-950/40",
      borderColor: "border-amber-400",
      condition:
        assessment.assessmentMethodology &&
        assessment.assessmentMethodology.length > 0,
    },
    {
      id: "feedback",
      title: tC(contentLanguage, "formativeAssessment.pedagogicalFeedback"),
      shortTitle: tC(
        contentLanguage,
        "formativeAssessment.pedagogicalFeedback",
      ),
      icon: Lightbulb,
      color: "#EAB308", // Yellow
      bgColor: "bg-yellow-500",
      gradient: "from-yellow-500 to-amber-600",
      darkGradient: "dark:from-yellow-600 dark:to-amber-700",
      lightBg: "bg-yellow-50",
      darkBg: "dark:bg-yellow-950/40",
      borderColor: "border-yellow-400",
      condition:
        assessment.pedagogicalFeedback &&
        assessment.pedagogicalFeedback.length > 0,
    },
    {
      id: "reflection",
      title: tC(contentLanguage, "formativeAssessment.learningReflection"),
      shortTitle: tC(contentLanguage, "formativeAssessment.learningReflection"),
      icon: GraduationCap,
      color: "#8B5CF6", // Purple
      bgColor: "bg-purple-500",
      gradient: "from-purple-500 to-violet-600",
      darkGradient: "dark:from-purple-600 dark:to-violet-700",
      lightBg: "bg-purple-50",
      darkBg: "dark:bg-purple-950/40",
      borderColor: "border-purple-400",
      condition:
        assessment.learningReflection &&
        assessment.learningReflection.length > 0,
    },
  ].filter((section) => section.condition);

  const scrollToSection = (sectionId: string) => {
    setActiveSection(sectionId);
  };

  const renderTabContent = () => {
    const activeAssessmentSection = assessmentSections.find(
      (section) => section.id === activeSection,
    );

    if (!activeAssessmentSection) {
      return (
        <div className="p-6 text-center text-gray-500 dark:text-gray-400">
          {tC(contentLanguage, "formativeAssessment.noContentAvailable")}
        </div>
      );
    }

    // Render content based on active section
    switch (activeSection) {
      case "pedagogical":
        if (!assessment.instructionalFlow?.pedagogical_approach) return null;
        return (
          <div className="space-y-6">
            <div className="flex items-center gap-4 mb-6">
              <div className="relative">
                <div className="p-3 rounded-2xl bg-gradient-to-br from-blue-100 to-indigo-100 dark:from-blue-900/60 dark:to-indigo-900/60 text-blue-600 dark:text-blue-400 shadow-md border border-blue-200/50 dark:border-blue-700/50">
                  <Brain size={28} className="drop-shadow-sm" />
                </div>
                <div className="absolute -top-1 -right-1 w-3 h-3 bg-blue-400 rounded-full animate-pulse"></div>
              </div>
              <div>
                <h3 className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-indigo-600 dark:from-blue-400 dark:to-indigo-400 bg-clip-text text-transparent">
                  {tC(
                    contentLanguage,
                    "formativeAssessment.pedagogicalApproach",
                  )}
                </h3>
                <div className="w-24 h-1 bg-gradient-to-r from-blue-400 to-indigo-400 rounded-full mt-2"></div>
              </div>
            </div>
            <div className="leading-relaxed text-gray-700 dark:text-gray-300">
              <EditableMarkdown
                value={assessment.instructionalFlow.pedagogical_approach || ""}
                isEditable={false}
                handleSave={async () => {
                  // API doesn't support updating instructionalFlow yet
                }}
              />
            </div>
          </div>
        );

      case "phases":
        if (
          !assessment.instructionalFlow?.learning_phases ||
          assessment.instructionalFlow.learning_phases.length === 0
        )
          return null;
        return (
          <div className="space-y-6">
            <div className="flex items-center gap-4 mb-6">
              <div className="relative">
                <div className="p-3 rounded-2xl bg-gradient-to-br from-emerald-100 to-green-100 dark:from-emerald-900/60 dark:to-green-900/60 text-emerald-600 dark:text-emerald-400 shadow-md border border-emerald-200/50 dark:border-emerald-700/50">
                  <ClipboardList size={28} className="drop-shadow-sm" />
                </div>
                <div className="absolute -top-1 -right-1 w-3 h-3 bg-emerald-400 rounded-full animate-pulse"></div>
              </div>
              <div>
                <h3 className="text-2xl font-bold bg-gradient-to-r from-emerald-600 to-green-600 dark:from-emerald-400 dark:to-green-400 bg-clip-text text-transparent">
                  {tC(contentLanguage, "formativeAssessment.learningPhases")}
                </h3>
                <div className="w-24 h-1 bg-gradient-to-r from-emerald-400 to-green-400 rounded-full mt-2"></div>
              </div>
            </div>
            <div className="space-y-6">
              {assessment.instructionalFlow.learning_phases.map(
                (phase, index) => (
                  <div key={index} className="relative group/phase">
                    <div className="absolute inset-0 bg-gradient-to-r from-emerald-100/50 to-green-100/50 dark:from-emerald-900/30 dark:to-green-900/30 rounded-xl opacity-0 group-hover/phase:opacity-100 transition-opacity duration-300"></div>
                    <div className="relative bg-gradient-to-br from-emerald-50 to-green-50 dark:from-emerald-900/30 dark:to-green-900/30 p-5 rounded-xl border-l-4 border-emerald-400 hover:border-emerald-500 transition-all duration-300 hover:shadow-md">
                      <div className="flex items-center gap-3 mb-3">
                        <div className="flex items-center gap-3 flex-1">
                          <div className="w-8 h-8 bg-gradient-to-br from-emerald-400 to-green-500 rounded-full flex items-center justify-center text-white font-bold text-sm shadow-md">
                            {index + 1}
                          </div>
                          <h5 className="font-bold text-emerald-700 dark:text-emerald-300 text-lg">
                            <EditableMarkdown
                              value={phase.phase_name || `Phase ${index + 1}`}
                              isEditable={false}
                              handleSave={async () => {}}
                            />
                          </h5>
                        </div>
                        {phase.duration && (
                          <div className="flex items-center gap-2">
                            <Target
                              size={16}
                              className="text-emerald-500 dark:text-emerald-400"
                            />
                            <span className="text-sm bg-gradient-to-r from-emerald-100 to-green-100 dark:from-emerald-800 dark:to-green-800 text-emerald-700 dark:text-emerald-300 px-3 py-1 rounded-full font-medium border border-emerald-200/50 dark:border-emerald-700/50">
                              <EditableMarkdown
                                value={phase.duration}
                                isEditable={false}
                                handleSave={async () => {}}
                              />
                            </span>
                          </div>
                        )}
                      </div>
                      {phase.instructional_narrative && (
                        <div className="text-gray-700 dark:text-gray-300 leading-relaxed pl-11 text-justify">
                          <EditableMarkdown
                            value={phase.instructional_narrative}
                            isEditable={false}
                            handleSave={async () => {
                              // API doesn't support updating instructionalFlow yet
                            }}
                          />
                        </div>
                      )}
                    </div>
                  </div>
                ),
              )}
            </div>
          </div>
        );

      case "methodology":
        if (
          !assessment.assessmentMethodology ||
          assessment.assessmentMethodology.length === 0
        )
          return null;
        return (
          <div className="space-y-6">
            <div className="flex items-center gap-4 mb-6">
              <div className="relative">
                <div className="p-3 rounded-2xl bg-gradient-to-br from-amber-100 to-orange-100 dark:from-amber-900/60 dark:to-orange-900/60 text-amber-600 dark:text-amber-400 shadow-md border border-amber-200/50 dark:border-amber-700/50">
                  <Target size={28} className="drop-shadow-sm" />
                </div>
                <div className="absolute -top-1 -right-1 w-3 h-3 bg-amber-400 rounded-full animate-pulse"></div>
              </div>
              <div>
                <h3 className="text-2xl font-bold bg-gradient-to-r from-amber-600 to-orange-600 dark:from-amber-400 dark:to-orange-400 bg-clip-text text-transparent">
                  {tC(
                    contentLanguage,
                    "formativeAssessment.assessmentMethodology",
                  )}
                </h3>
                <div className="w-24 h-1 bg-gradient-to-r from-amber-400 to-orange-400 rounded-full mt-2"></div>
              </div>
            </div>
            <div className="leading-relaxed">
              <div className="grid gap-4">
                {assessment.assessmentMethodology.map((method, index) => (
                  <div key={index} className="group/method relative">
                    <div className="absolute inset-0 bg-gradient-to-r from-amber-100/30 to-orange-100/30 dark:from-amber-900/20 dark:to-orange-900/20 rounded-lg opacity-0 group-hover/method:opacity-100 transition-opacity duration-300"></div>
                    <div className="relative flex items-start gap-4 p-4 bg-gradient-to-r from-amber-50/50 to-orange-50/50 dark:from-amber-900/20 dark:to-orange-900/20 rounded-lg border border-amber-200/30 dark:border-amber-700/30 hover:border-amber-300/50 dark:hover:border-amber-600/50 transition-all duration-300">
                      <div className="flex-shrink-0 w-6 h-6 bg-gradient-to-br from-amber-400 to-orange-500 rounded-full flex items-center justify-center text-white font-bold text-xs shadow-sm">
                        {index + 1}
                      </div>
                      <div className="flex-1 text-gray-700 dark:text-gray-300">
                        <EditableMarkdown
                          value={method}
                          isEditable={false}
                          handleSave={async () => {}}
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        );

      case "feedback":
        if (
          !assessment.pedagogicalFeedback ||
          assessment.pedagogicalFeedback.length === 0
        )
          return null;
        return (
          <div className="space-y-6">
            <div className="flex items-center gap-4 mb-6">
              <div className="relative">
                <div className="p-3 rounded-2xl bg-gradient-to-br from-yellow-100 to-amber-100 dark:from-yellow-900/60 dark:to-amber-900/60 text-yellow-600 dark:text-yellow-400 shadow-md border border-yellow-200/50 dark:border-yellow-700/50">
                  <Lightbulb size={28} className="drop-shadow-sm" />
                </div>
                <div className="absolute -top-1 -right-1 w-3 h-3 bg-yellow-400 rounded-full animate-pulse"></div>
              </div>
              <div>
                <h3 className="text-2xl font-bold bg-gradient-to-r from-yellow-600 to-amber-600 dark:from-yellow-400 dark:to-amber-400 bg-clip-text text-transparent">
                  {tC(
                    contentLanguage,
                    "formativeAssessment.pedagogicalFeedback",
                  )}
                </h3>
                <div className="w-24 h-1 bg-gradient-to-r from-yellow-400 to-amber-400 rounded-full mt-2"></div>
              </div>
            </div>
            <div className="leading-relaxed">
              <div className="grid gap-4">
                {assessment.pedagogicalFeedback.map((feedback, index) => (
                  <div key={index} className="group/feedback relative">
                    <div className="absolute inset-0 bg-gradient-to-r from-yellow-100/30 to-amber-100/30 dark:from-yellow-900/20 dark:to-amber-900/20 rounded-lg opacity-0 group-hover/feedback:opacity-100 transition-opacity duration-300"></div>
                    <div className="relative flex items-start gap-4 p-4 bg-gradient-to-r from-yellow-50/50 to-amber-50/50 dark:from-yellow-900/20 dark:to-amber-900/20 rounded-lg border border-yellow-200/30 dark:border-yellow-700/30 hover:border-yellow-300/50 dark:hover:border-yellow-600/50 transition-all duration-300">
                      <div className="flex-shrink-0 w-6 h-6 bg-gradient-to-br from-yellow-400 to-amber-500 rounded-full flex items-center justify-center text-white shadow-sm">
                        <MessageSquare size={12} className="drop-shadow-sm" />
                      </div>
                      <div className="flex-1 text-gray-700 dark:text-gray-300">
                        <EditableMarkdown
                          value={feedback}
                          isEditable={false}
                          handleSave={async () => {}}
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        );

      case "reflection":
        if (
          !assessment.learningReflection ||
          assessment.learningReflection.length === 0
        )
          return null;
        return (
          <div className="space-y-6">
            <div className="flex items-center gap-4 mb-6">
              <div className="relative">
                <div className="p-3 rounded-2xl bg-gradient-to-br from-purple-100 to-violet-100 dark:from-purple-900/60 dark:to-violet-900/60 text-purple-600 dark:text-purple-400 shadow-md border border-purple-200/50 dark:border-purple-700/50">
                  <GraduationCap size={28} className="drop-shadow-sm" />
                </div>
                <div className="absolute -top-1 -right-1 w-3 h-3 bg-purple-400 rounded-full animate-pulse"></div>
              </div>
              <div>
                <h3 className="text-2xl font-bold bg-gradient-to-r from-purple-600 to-violet-600 dark:from-purple-400 dark:to-violet-400 bg-clip-text text-transparent">
                  {tC(
                    contentLanguage,
                    "formativeAssessment.learningReflection",
                  )}
                </h3>
                <div className="w-24 h-1 bg-gradient-to-r from-purple-400 to-violet-400 rounded-full mt-2"></div>
              </div>
            </div>
            <div className="leading-relaxed">
              <div className="grid gap-4">
                {assessment.learningReflection.map((reflection, index) => (
                  <div key={index} className="group/reflection relative">
                    <div className="absolute inset-0 bg-gradient-to-r from-purple-100/30 to-violet-100/30 dark:from-purple-900/20 dark:to-violet-900/20 rounded-lg opacity-0 group-hover/reflection:opacity-100 transition-opacity duration-300"></div>
                    <div className="relative flex items-start gap-4 p-4 bg-gradient-to-r from-purple-50/50 to-violet-50/50 dark:from-purple-900/20 dark:to-violet-900/20 rounded-lg border border-purple-200/30 dark:border-purple-700/30 hover:border-purple-300/50 dark:hover:border-purple-600/50 transition-all duration-300">
                      <div className="flex-shrink-0 w-6 h-6 bg-gradient-to-br from-purple-400 to-violet-500 rounded-full flex items-center justify-center text-white shadow-sm">
                        <Award size={12} className="drop-shadow-sm" />
                      </div>
                      <div className="flex-1 text-gray-700 dark:text-gray-300">
                        <EditableMarkdown
                          value={reflection}
                          isEditable={false}
                          handleSave={async () => {}}
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        );

      default:
        return (
          <div className="p-6 text-center text-gray-500 dark:text-gray-400">
            {tC(contentLanguage, "formativeAssessment.selectTab")}
          </div>
        );
    }
  };

  // Set the first available section as active on mount
  useEffect(() => {
    if (
      assessmentSections.length > 0 &&
      !assessmentSections.find((s) => s.id === activeSection)
    ) {
      setActiveSection(assessmentSections[0].id);
    }
  }, [assessmentSections, activeSection]);

  return (
    <div
      data-testid="formative-assessment-card"
      className="min-h-fit bg-gradient-to-br from-amber-50 via-yellow-50 to-orange-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 py-8"
    >
      {/* Decorative background elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-10 left-10 w-48 h-48 bg-gradient-to-br from-yellow-200/20 to-amber-300/20 dark:from-yellow-900/15 dark:to-amber-900/15 rounded-full blur-3xl"></div>
        <div className="absolute top-32 right-10 w-64 h-64 bg-gradient-to-br from-orange-200/15 to-red-300/15 dark:from-orange-900/10 dark:to-red-900/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-10 left-1/2 w-52 h-52 bg-gradient-to-br from-amber-200/20 to-yellow-300/20 dark:from-amber-900/15 dark:to-yellow-900/15 rounded-full blur-3xl"></div>
      </div>

      <div className="container max-w-7xl mx-auto px-4 py-4 relative z-10">
        {assessment.imageUrl && (
          <div>
            <CardImage
              createdAt={assessment.createdAt}
              imageUrl={assessment.imageUrl}
              videoUrl={assessment.videoUrl}
              courseName={assessment.courseName}
              gradeLevel={assessment.gradeLevel}
              theme="yellow"
              topic={
                <div className="flex items-center gap-4 justify-center">
                  {assessment.title && (
                    <>
                      <div className="p-4 rounded-2xl bg-gradient-to-br from-yellow-100 to-amber-100 dark:from-yellow-900/50 dark:to-amber-900/50 text-yellow-600 dark:text-yellow-400 shadow-lg border border-yellow-200/50 dark:border-yellow-700/50">
                        <BookMinusIcon size={32} className="drop-shadow-sm" />
                      </div>
                      <div className="text-center">
                        <div className="mb-2 text-3xl md:text-4xl font-bold bg-gradient-to-r from-yellow-700 to-amber-700 dark:from-yellow-400 dark:to-amber-400 bg-clip-text text-transparent leading-tight">
                          {assessment.title?.replace(/\*\*/g, "") || ""}
                        </div>
                      </div>
                    </>
                  )}
                </div>
              }
              contentLanguage={contentLanguage}
            />
          </div>
        )}

        {/* Enhanced Action Buttons */}
        <div className="flex justify-end mb-6 gap-3">
          {onCreateAgain && (
            <Button
              onClick={onCreateAgain}
              variant="solid"
              size="md"
              textColor="text-white dark:text-white"
              bgColor="bg-gradient-to-r from-yellow-500 via-amber-500 to-orange-500 hover:from-yellow-600 hover:via-amber-600 hover:to-orange-600 dark:hover:from-yellow-600 dark:hover:via-amber-600 dark:hover:to-orange-600 shadow-lg hover:shadow-xl transition-all duration-300"
              rounded="xl"
              className="transform hover:scale-105 hover:-translate-y-1 transition-all duration-300 border border-yellow-400/30"
            >
              <RefreshCw size={18} className="animate-pulse" />
              <span className="font-semibold">
                {tC(contentLanguage, "common.createAnother")}
              </span>
            </Button>
          )}
          <div>
            <PdfDownloadButton
              pdfTitle={assessment.title ?? ""}
              openCustomizationModal={() => setShowPdfCustomizationModal(true)}
              document={
                <FormativeAssessmentCardPDF
                  customizationSettings={customizationSettings as any}
                  assessment={{ ...assessment, imageUrl: finalImageUrl }}
                />
              }
              btnTheme="bg-gradient-to-r from-yellow-500 via-amber-500 to-orange-500 shadow-lg hover:shadow-xl border border-yellow-400/30"
            />
          </div>
        </div>

        {/* Tab-based Content like BloomTaxonomyCard */}
        <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-lg rounded-2xl shadow-xl border border-gray-200/50 dark:border-gray-700/50">
          {/* Tab Navigation */}
          {assessmentSections.length > 0 && (
            <div className="sticky top-0 z-50 rounded-2xl">
              <div className="flex flex-col gap-2 p-4 bg-gradient-to-r from-amber-50 to-yellow-50 dark:from-gray-800 dark:to-gray-700 rounded-t-2xl">
                <div className="flex flex-wrap justify-center gap-2 w-full">
                  {assessmentSections.map((section) => {
                    const IconComponent = section.icon;
                    const isActive = activeSection === section.id;
                    const bgGradient = `bg-gradient-to-r ${section.gradient} ${section.darkGradient}`;

                    return (
                      <button
                        key={section.id}
                        onClick={() => scrollToSection(section.id)}
                        className={`flex items-center justify-center gap-2 px-4 py-3 rounded-xl text-sm font-medium transition-all duration-300 ${
                          isActive
                            ? bgGradient +
                              " text-white shadow-lg transform scale-105"
                            : `bg-white/50 dark:bg-gray-800/50 hover:bg-opacity-10 text-gray-700 dark:text-gray-200 hover:shadow-md shadow-sm hover:scale-105`
                        }`}
                      >
                        <IconComponent size={18} />
                        <span className="hidden md:inline">
                          {section.shortTitle}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          {/* Tab Content */}
          <div className="p-6">{renderTabContent()}</div>
        </div>
      </div>
      {/* PDF Customization Modal */}
      <PdfCustomizationModal
        show={showPdfCustomizationModal}
        onClose={() => setShowPdfCustomizationModal(false)}
        hideSchoolLogoOptions
        documentComponent={
          <FormativeAssessmentCardPDF
            customizationSettings={customizationSettings as any}
            assessment={{ ...assessment, imageUrl: finalImageUrl }}
          />
        }
      />
    </div>
  );
};

export default FormativeAssessmentCard;
